/**
 * Leg Editor Component
 * Edits a single flight leg or travel segment
 */

import React, { useState, useCallback } from 'react';
import { getSchema, getFieldLabel, getFieldType } from '../../utils/fieldSchemas';
import { getAirportDisplay } from '../../utils/airportCodes';

const LegEditor = ({
  legNumber,
  data = {},
  documentType = 'flight',
  confidence = {},
  onChange,
  onRemove,
  disabled = false,
  canRemove = true,
  className = ''
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const schema = getSchema(documentType);
  const legFields = schema.legFields || { required: [], optional: [] };
  const allLegFields = [...(legFields.required || []), ...(legFields.optional || [])];

  // Handle field change
  const handleChange = useCallback((field, value) => {
    onChange?.({ ...data, [field]: value });
  }, [data, onChange]);

  // Get display value for route
  const getRouteDisplay = () => {
    const from = data.departure_airport || data.departure_airport_icao || '?';
    const to = data.arrival_airport || data.arrival_airport_icao || '?';
    return `${from} → ${to}`;
  };

  // Get display value for date/time
  const getDateTimeDisplay = () => {
    const date = data.departure_date?.slice(0, 10) || '';
    const time = data.departure_time?.slice(0, 5) || '';
    if (date && time) return `${date} ${time}`;
    if (date) return date;
    return '';
  };

  // Get flight number display
  const getFlightDisplay = () => {
    if (data.flight_number) {
      const airline = data.airline_code || data.airline || '';
      return airline ? `${airline} ${data.flight_number}` : data.flight_number;
    }
    return '';
  };

  return (
    <div className={`border border-gray-200 rounded-lg overflow-hidden ${className}`}>
      {/* Header - always visible */}
      <div
        className={`flex items-center justify-between px-3 py-2 cursor-pointer ${
          isExpanded ? 'bg-gray-50 border-b border-gray-200' : 'bg-white hover:bg-gray-50'
        }`}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          <span className="text-xs font-medium text-gray-500">
            Leg {legNumber}
          </span>
          <span className="text-sm font-medium text-gray-900">
            {getRouteDisplay()}
          </span>
          {getFlightDisplay() && (
            <span className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
              {getFlightDisplay()}
            </span>
          )}
          {getDateTimeDisplay() && (
            <span className="text-xs text-gray-500">
              {getDateTimeDisplay()}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {canRemove && !disabled && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onRemove?.();
              }}
              className="p-1 text-gray-400 hover:text-red-500 rounded"
              title="Remove leg"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          )}
          <svg
            className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {/* Expandable fields */}
      {isExpanded && (
        <div className="p-3 space-y-3">
          {/* Route row */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">
                {getFieldLabel(documentType, 'departure_airport')}
              </label>
              <input
                type="text"
                value={data.departure_airport || data.departure_airport_icao || ''}
                onChange={(e) => {
                  const field = documentType === 'private_flight' ? 'departure_airport_icao' : 'departure_airport';
                  handleChange(field, e.target.value.toUpperCase());
                }}
                disabled={disabled}
                placeholder="e.g., JFK"
                className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">
                {getFieldLabel(documentType, 'arrival_airport')}
              </label>
              <input
                type="text"
                value={data.arrival_airport || data.arrival_airport_icao || ''}
                onChange={(e) => {
                  const field = documentType === 'private_flight' ? 'arrival_airport_icao' : 'arrival_airport';
                  handleChange(field, e.target.value.toUpperCase());
                }}
                disabled={disabled}
                placeholder="e.g., LAX"
                className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Date/Time row */}
          <div className="grid grid-cols-4 gap-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Departure Date</label>
              <input
                type="date"
                value={data.departure_date?.slice(0, 10) || ''}
                onChange={(e) => handleChange('departure_date', e.target.value)}
                disabled={disabled}
                className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Dep. Time</label>
              <input
                type="time"
                value={data.departure_time?.slice(0, 5) || ''}
                onChange={(e) => handleChange('departure_time', e.target.value)}
                disabled={disabled}
                className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Arrival Date</label>
              <input
                type="date"
                value={data.arrival_date?.slice(0, 10) || ''}
                onChange={(e) => handleChange('arrival_date', e.target.value)}
                disabled={disabled}
                className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Arr. Time</label>
              <input
                type="time"
                value={data.arrival_time?.slice(0, 5) || ''}
                onChange={(e) => handleChange('arrival_time', e.target.value)}
                disabled={disabled}
                className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Flight details row */}
          {documentType !== 'private_flight' && (
            <div className="grid grid-cols-4 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Airline</label>
                <input
                  type="text"
                  value={data.airline || ''}
                  onChange={(e) => handleChange('airline', e.target.value)}
                  disabled={disabled}
                  placeholder="e.g., United"
                  className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Flight #</label>
                <input
                  type="text"
                  value={data.flight_number || ''}
                  onChange={(e) => handleChange('flight_number', e.target.value)}
                  disabled={disabled}
                  placeholder="e.g., 123"
                  className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Seat</label>
                <input
                  type="text"
                  value={data.seat || ''}
                  onChange={(e) => handleChange('seat', e.target.value)}
                  disabled={disabled}
                  placeholder="e.g., 12A"
                  className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Class</label>
                <input
                  type="text"
                  value={data.class_type || data.cabin_class || ''}
                  onChange={(e) => handleChange('class_type', e.target.value)}
                  disabled={disabled}
                  placeholder="e.g., Economy"
                  className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
            </div>
          )}

          {/* Private flight specific fields */}
          {documentType === 'private_flight' && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Departure FBO</label>
                <input
                  type="text"
                  value={data.departure_fbo_name || ''}
                  onChange={(e) => handleChange('departure_fbo_name', e.target.value)}
                  disabled={disabled}
                  placeholder="e.g., Signature Flight"
                  className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Arrival FBO</label>
                <input
                  type="text"
                  value={data.arrival_fbo_name || ''}
                  onChange={(e) => handleChange('arrival_fbo_name', e.target.value)}
                  disabled={disabled}
                  placeholder="e.g., Atlantic Aviation"
                  className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LegEditor;
